
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import React from 'react';

console.log('Main.tsx is loading...');

// Create a root using createRoot API
const rootElement = document.getElementById("root");
if (!rootElement) {
  console.error('Root element not found');
  throw new Error('Root element not found');
}

console.log('Root element found, loading full app');

// Simple error boundary to catch and display runtime errors
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: Error | null}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    console.error("Error caught by ErrorBoundary:", error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("React ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', color: 'white', backgroundColor: '#333' }}>
          <h1>Something went wrong</h1>
          <p>{this.state.error?.message || 'Unknown error'}</p>
          <pre style={{ fontSize: '12px', overflow: 'auto' }}>{this.state.error?.stack}</pre>
          <button 
            style={{ padding: '8px 16px', marginTop: '20px', cursor: 'pointer' }}
            onClick={() => this.setState({ hasError: false })}
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Ensure DOM is ready and scrolling works
const ensureScrollingWorks = () => {
  return new Promise<void>((resolve) => {
    const checkScrolling = () => {
      // Force body overflow to auto if it's been changed by external scripts
      document.body.style.overflowY = 'auto';
      
      // Check if body has proper scroll capability
      const bodyStyles = window.getComputedStyle(document.body);
      const hasScrolling = bodyStyles.overflowY === 'auto' || bodyStyles.overflowY === 'scroll';
      
      if (hasScrolling && document.readyState === 'complete') {
        resolve();
      } else {
        requestAnimationFrame(checkScrolling);
      }
    };
    
    if (document.readyState === 'complete') {
      // Final defensive scroll protection
      document.body.style.overflowY = 'auto';
      resolve();
    } else {
      checkScrolling();
    }
  });
};

// Load analytics scripts after app is ready
const loadDeferredAnalytics = () => {
  console.log('Loading deferred analytics scripts...');
  
  // Load Umami — self-hosted at z.harmar.ai since 2026-09-06 (was Umami Cloud).
  //
  // data-host-url IS REQUIRED here and was not before. The two tracker builds
  // differ in their fallback: the Cloud script hardcodes one
  // (`hostUrl || "https://gateway.umami.is"`), which is why this worked with
  // no attribute set. The SELF-HOSTED build has no such constant and derives
  // the collector from its own src instead. It happens to resolve to the same
  // host today, but the moment this script is ever served from a proxy path or
  // a CDN the events would POST to that origin, 404, and report nothing — no
  // console error, no failure, just a dashboard that stops filling up. Stating
  // it explicitly costs one line and removes the trap.
  //
  // The website id is UNCHANGED: the self-hosted site was created with the
  // same id the Cloud one had, so historical data and this tag still agree.
  const UMAMI_HOST = 'https://z.harmar.ai';
  const umamiScript = document.createElement('script');
  umamiScript.defer = true;
  umamiScript.src = `${UMAMI_HOST}/script.js`;
  umamiScript.setAttribute('data-website-id', '21737dc4-03d6-4855-ba4e-5b6e3f6a08e9');
  umamiScript.setAttribute('data-host-url', UMAMI_HOST);
  document.head.appendChild(umamiScript);
  
  // Load Microsoft Clarity
  const clarityScript = document.createElement('script');
  clarityScript.innerHTML = `
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "sfye6efrx6");
  `;
  document.head.appendChild(clarityScript);
  
  // Load Facebook Pixel
  const fbPixelScript = document.createElement('script');
  fbPixelScript.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '805249521956122');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(fbPixelScript);
  
  console.log('Analytics scripts loaded after app initialization');
};

try {
  console.log('Creating React root...');
  const root = createRoot(rootElement);
  console.log('React root created successfully');
  
  // Ensure scrolling works before rendering
  ensureScrollingWorks().then(() => {
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </React.StrictMode>
    );
    console.log('App rendered successfully with scroll protection');
    
    // Load analytics after app is fully rendered
    setTimeout(() => {
      loadDeferredAnalytics();
    }, 1000); // 1 second delay to ensure app is interactive
  });
} catch (error) {
  console.error('Failed to render application:', error);
  
  rootElement.innerHTML = `
    <div style="padding: 20px; color: white; background-color: #333;">
      <h1>Failed to load application</h1>
      <p>${error instanceof Error ? error.message : 'Unknown error'}</p>
      <pre>${error instanceof Error ? error.stack : ''}</pre>
    </div>
  `;
}
